import React from 'react';
import {useMetrics} from '@sail/observability';

interface IComponentLoadMetricContext {
  reportComponentFinishedLoading: () => void;
  preventComponentLoadReport: () => void;
}

const ComponentLoadMetricContext =
  React.createContext<IComponentLoadMetricContext>(
    // Empty report functions
    {
      reportComponentFinishedLoading: () => {},
      preventComponentLoadReport: () => {},
    },
  );
ComponentLoadMetricContext.displayName = 'MetricsContext';

type Props = {
  initialRenderTime: number;
  connectJsLoadStartTime?: number;
};

export const ComponentLoadMetricContextProvider: React.FC<Props> = ({
  initialRenderTime,
  connectJsLoadStartTime,
  children,
}) => {
  const hasReportedLoaded = React.useRef<boolean>(false);
  const metrics = useMetrics();

  const reportComponentFinishedLoading = () => {
    // Avoid reporting loaded multiple times for a given component tree
    if (hasReportedLoaded.current) {
      return;
    }
    hasReportedLoaded.current = true;

    // eslint-disable-next-line @stripe-internal/embedded/no-restricted-globals
    const currentTime = window.performance.now();
    metrics.gauge('connect_element_ui_ready', {
      duration: Math.ceil(currentTime - initialRenderTime),
    });
    if (connectJsLoadStartTime) {
      metrics.gauge('connect_js_load_to_ui_ready', {
        duration: Math.ceil(currentTime - connectJsLoadStartTime),
      });
    }
  };

  const preventComponentLoadReport = () => {
    // Mark this as a session where the component will not load. This can happen due to errors loading or different component
    // loading paths (i.e. onboarding)
    hasReportedLoaded.current = true;
  };
  return (
    <ComponentLoadMetricContext.Provider
      value={{reportComponentFinishedLoading, preventComponentLoadReport}}
    >
      {children}
    </ComponentLoadMetricContext.Provider>
  );
};

export const useComponentLoadMetric = (): IComponentLoadMetricContext => {
  const componentLoadMetricContext = React.useContext(
    ComponentLoadMetricContext,
  );

  return componentLoadMetricContext;
};
